import { Text } from "@asayinc/component-library";
import { Box, IconButton } from "@mui/material";
import { useParams } from "react-router-dom";
import { Preview } from "@mui/icons-material";
import { Card } from "src/components/atoms/Card";
import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LabelField } from "src/components/molecules/LabelField";
import { LoadingCard } from "src/components/molecules/Loading";
import { useBankruptcyEventDocumentsQuery } from "src/store/bankruptcyEvent";
import { LabelFieldWithIcon } from "src/components/molecules/LabelFieldWithIcon";
import { MaterialsView } from "src/components/organisms/MaterialsView";
import { IDocuments } from "src/store/types";
import { useState } from "react";

export function BankruptcyEventMaterialsTab() {
  const { id } = useParams() as { id: string };
  const {
    data: bankruptcyEventMaterials,
    isLoading,
    error,
  } = useBankruptcyEventDocumentsQuery({ id });

  const [selectedMaterial, setSelectedMaterial] = useState<IDocuments | null>(
    null
  );

  const handleMaterialClick = (material: IDocuments) => {
    setSelectedMaterial(material);
  };

  if (error || (!isLoading && !bankruptcyEventMaterials)) {
    return <ErrorState />;
  }

  if (isLoading) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <Box sx={{ display: "flex", mt: 0 }}>
          <Box sx={{ flexGrow: 1, flexBasis: 0, mr: 12 }}>
            <LoadingCard />
          </Box>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <LoadingCard />
          </Box>
        </Box>
      </ScrollContainer>
    );
  }

  const hasMaterials = bankruptcyEventMaterials.length > 0;

  return (
    <ScrollContainer sx={{ pb: 10, pl: 10, pr: 6.25, height: "100%" }}>
      <Box sx={{ display: "flex", mt: 8, height: "100%" }}>
        <Box sx={{ flexGrow: 1, flexBasis: 0, mr: hasMaterials ? 12 : 0 }}>
          <Card title="Bankruptcy Event Materials">
            {!hasMaterials ? (
              <Box sx={{ py: 5, px: 4 }}>
                <Text variant="body2" sx={{ color: "text.secondary" }}>
                  No materials available
                </Text>
              </Box>
            ) : (
              bankruptcyEventMaterials.map((material) => (
                <Box key={material.id}>
                  <LabelFieldWithIcon
                    sx={{
                      py: 5,
                      pl: 4,
                      borderBottom: "1px solid #e0e0e0",
                      fontWeight: 600,
                    }}
                    isLabelBold={true}
                    label={material.name}
                    icon={
                      <IconButton onClick={() => handleMaterialClick(material)}>
                        <Preview style={{ color: "black" }} />
                      </IconButton>
                    }
                  />
                  <LabelField
                    sx={{
                      py: 5,
                      px: 4,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                    isValueBold={false}
                    label={material.description}
                    value={""}
                  />
                </Box>
              ))
            )}
          </Card>
        </Box>
        {hasMaterials && (
          <Box sx={{ flexGrow: 1, flexBasis: 0, height: "100%" }}>
            <Card title="Materials Preview" sx={{ pb: 15, height: "100%" }}>
              {selectedMaterial ? (
                <MaterialsView
                  id={selectedMaterial.id}
                  url={selectedMaterial.url}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 10,
                    px: 4,
                    color: "text.secondary",
                  }}
                >
                  <Preview sx={{ fontSize: 48, mb: 2, opacity: 0.4 }} />
                  <Text
                    variant="body2"
                    sx={{ textAlign: "center", color: "text.secondary" }}
                  >
                    Click the preview icon next to a material to view it here
                  </Text>
                </Box>
              )}
            </Card>
          </Box>
        )}
      </Box>
    </ScrollContainer>
  );
}
